import React, { useState, useEffect } from 'react';
import { Header } from '../components/Header';
import { Hero } from '../components/Hero';
import { About } from '../components/About';
import { Features } from '../components/Features';
import { CowCare } from '../components/CowCare';
import { Testimonials } from '../components/Testimonials';
import { CTA } from '../components/CTA';
import { Footer } from '../components/Footer';
import { LanguagePopup } from '../components/LanguagePopup';
import { translations } from '../mockData';

export const LandingPage = () => {
  const [language, setLanguage] = useState(null);
  const [showLanguagePopup, setShowLanguagePopup] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);

  useEffect(() => {
    // Check if language preference exists in localStorage
    const savedLanguage = localStorage.getItem('rajdarshan_language');
    if (savedLanguage) {
      setLanguage(savedLanguage);
    } else {
      // Show popup if no language preference is saved
      setShowLanguagePopup(true);
    }

    // Preload heavy assets
    const assetsToLoad = [
      '/milkdropCOMPRESSED.mp4',
      '/desimilkincanCOMPRESSED.mp4',
      '/COMPRESSEDprocessing.mp4',
      '/milkporCOMPRESSED.mp4',
      '/cowgrazingCOMPRESSED.mp4',
      '/desimilkextractionCOMPRESSED.mp4',
      '/logo.png'
    ];

    let loadedAssets = 0;

    const preloadAsset = (src) => {
      return new Promise((resolve) => {
        if (src.endsWith('.mp4')) {
          const v = document.createElement('video');
          v.preload = 'auto'; // Force rigorous preloading
          v.src = src;
          v.oncanplaythrough = () => {
            loadedAssets++;
            setLoadingProgress(Math.floor((loadedAssets / assetsToLoad.length) * 100));
            resolve(true);
          };
          v.onerror = () => resolve(true); // fall back gracefully
          v.load();
        } else {
          const img = new Image();
          img.src = src;
          img.onload = () => {
            loadedAssets++;
            setLoadingProgress(Math.floor((loadedAssets / assetsToLoad.length) * 100));
            resolve(true);
          };
          img.onerror = () => resolve(true);
        }
      });
    };

    Promise.all(assetsToLoad.map(preloadAsset)).then(() => {
      // Ensure the loader stays for at least a minimum time for smooth UX
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    });

    // Fallback in case of slow networks to not keep users blocked forever
    const fallbackTimeout = setTimeout(() => {
      setIsLoading(false);
    }, 15000); // 15 seconds max

    return () => clearTimeout(fallbackTimeout);
  }, []);

  const handleLanguageSelect = (selectedLanguage) => {
    setLanguage(selectedLanguage);
    localStorage.setItem('rajdarshan_language', selectedLanguage);
    setShowLanguagePopup(false);
  };

  const handleLanguageChange = (newLanguage) => {
    setLanguage(newLanguage);
    localStorage.setItem('rajdarshan_language', newLanguage);
  };

  // Don't render content until language is selected
  if (!language && !isLoading) {
    return (
      <LanguagePopup
        open={showLanguagePopup}
        onLanguageSelect={handleLanguageSelect}
        translations={translations}
      />
    );
  }

  // Preloader Screen
  if (isLoading || !language) {
    return (
      <div className="fixed inset-0 z-[100] bg-[#1A4A8F] flex flex-col items-center justify-center transition-opacity duration-500">
        <div className="bg-white/10 p-4 rounded-xl backdrop-blur-md mb-8 animate-pulse">
          <img src="/logo.png" alt="RajDarshan Farms Logo" className="w-32 h-auto" />
        </div>

        {/* Progress Bar Container */}
        <div className="w-64 h-2 bg-white/20 rounded-full overflow-hidden">
          <div
            className="h-full bg-white transition-all duration-300 ease-out"
            style={{ width: `${loadingProgress}%` }}
          />
        </div>
        <p className="text-white mt-4 font-serif font-semibold text-lg tracking-widest animate-pulse">
          {loadingProgress}%
        </p>
        <p className="text-white/70 mt-2 text-sm">Preparing Freshness...</p>
      </div>
    );
  }

  const t = translations[language];

  return (
    <div className="min-h-screen">
      <Header
        language={language}
        setLanguage={handleLanguageChange}
        t={t}
        showLanguageToggle={true}
      />
      <main>
        <Hero t={t} />
        <About t={t} />
        <Features t={t} />
        <CowCare t={t} />
        <Testimonials t={t} />
        <CTA t={t} />
      </main>
      <Footer t={t} />
    </div>
  );
};
